import { ApiResponse, GlobalStore } from "@roc/feature-app-core";
import { action, flow, makeObservable, observable } from "mobx";
import { AppraisalReassignmentLetterService } from "../../services/documentForms/appraisalReassignmentLetterService";
import { getMultiPageScreenshotGridWaitingForImages } from "@roc/ui";

export interface AppraisalReassignmentLetterDetails {
  lenderAlias: string,
  propertyAddress: string,
  borrowerName: string,
  lenderName: string,
  documentSigned: boolean,
  signedDate: string,
}

export class AppraisalReassignmentLetterStore {
  private globalStore: GlobalStore;
  private appraisalReassignmentLetterService: AppraisalReassignmentLetterService;
  loanId: number;
  propertyId: number;
  appraisalReassignmentLetterDetails: AppraisalReassignmentLetterDetails;
  isSigned: boolean;
  screenshotElementIds: string[];
  isPdfPreviewModalOpen: boolean;
  pdfTitle: string;
  pdfData: any;

  constructor(
    globalStore: GlobalStore
  ){
    this.globalStore = globalStore;
    this.appraisalReassignmentLetterService = new AppraisalReassignmentLetterService();
    this.screenshotElementIds = ['letter'];
    makeObservable(this, {
      loanId: observable,
      propertyId: observable,
      resetStore: action,
      getAppraisalReassignmentLetterData: flow,
      appraisalReassignmentLetterDetails: observable,
      sign: flow,
      isSigned: observable,
      isPdfPreviewModalOpen: observable,
      pdfTitle: observable,
      pdfData: observable,
      downloadSignedAppraisalLetter: flow,
    })
  }

  resetStore(){
    this.loanId = null;
    this.propertyId = null;
    this.appraisalReassignmentLetterDetails = null;
    this.isSigned = false;
  }

  *getAppraisalReassignmentLetterData(loanId, propertyId){
    try {
      this.loanId = loanId;
      this.propertyId = propertyId;
      const response = yield this.appraisalReassignmentLetterService.getAppraisalLetterInformation(loanId, propertyId);
      this.appraisalReassignmentLetterDetails = response.data;
      this.isSigned = this.appraisalReassignmentLetterDetails.documentSigned;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({message: 'Error while loading letter details'});
    }
  }

  *sign(loanId: number, propertyId: number, userId: number, signature: string) {
    const fileName = `Appraisal Reassignment Letter-${propertyId}`;
    this.showScreenshotGrid();
    const dataWithPdfAttached: FormData = yield getMultiPageScreenshotGridWaitingForImages(
      fileName,
      this.screenshotElementIds,
      []
    );
    this.hideScreenshotGrid();

    const data = {
      loanId: loanId,
      propertyId: propertyId,
      signature: signature,
      userAgent: navigator.userAgent,
      userId: userId,
    };

    dataWithPdfAttached.append(
      'data',
      new Blob([JSON.stringify(data)], {type: 'application/json'})
    );

    const response: ApiResponse = yield this.appraisalReassignmentLetterService.sign(dataWithPdfAttached);

    this.isSigned = true;
    this.globalStore.notificationStore.showSuccessNotification({
      message: 'Your signature was saved successfully.'
    })
  }

  showScreenshotGrid() {
    const parentTag = document.getElementById('screenshotParent');
    if (parentTag) {
      parentTag.style.position = 'absolute';
      parentTag.style.top = '0';
      parentTag.style.zIndex = '-9999';
      parentTag.style.display = 'block';
      parentTag.style.width = '841px';
    }
  }

  hideScreenshotGrid() {
    const parentTag = document.getElementById('screenshotParent');
    if (parentTag) {
      parentTag.style.display = 'none';
    }
  }

  *downloadSignedAppraisalLetter(loanId: number, propertyId: number) {
    try{
      const blob = yield this.appraisalReassignmentLetterService.downloadAppraisalLetter(loanId, propertyId);
      const reader = new FileReader();
      reader.readAsArrayBuffer(blob);
      reader.onloadend = () => {
        this.pdfTitle = 'Appraisal Reassignment Letter - '+this.appraisalReassignmentLetterDetails.propertyAddress;
        this.pdfData = reader.result;
        this.isPdfPreviewModalOpen = true;
      }

    }catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error downloading the Appraisal Letter'
      })
    }
  }

}